import { Alert, Box, Popover, Stack } from "@mui/material";
import { sortBy } from "lodash-es";
import { useMemo } from "react";
import { EmojiChip } from "../../../../../modules/moment/component/emoji/emoji-view";
import { usePopover } from "../../../../../utils/hooks/use-popover";
import { useMomentAnalyticsContext } from "../../context/moment-stats-context";

import type { MomentAnalyticsTotals } from "../../../../../modules/stats/server/analytics-service";
import type { FC } from "react";

export const AnalyticsOverviewReactions: FC<{
  reactions: MomentAnalyticsTotals["reaction_summary"];
}> = ({ reactions }) => {
  const [anchorRef, popover] = usePopover();

  const { emojiCache } = useMomentAnalyticsContext();

  const sorted = useMemo(
    () => sortBy(Object.entries(reactions), ([, { count }]) => -count),
    [reactions],
  );

  const shouldTruncate = sorted.length > 3;

  if (sorted.length === 0)
    return (
      <Alert severity="info" icon={false}>
        No reactions yet
      </Alert>
    );

  return (
    <>
      <Stack direction="row" spacing={1} width="100%">
        {sorted.slice(0, 3).map(([emoji, { count }]) => (
          <EmojiChip key={emoji} emoji={emoji} emojiCacheEntry={emojiCache[emoji]} label={count} />
        ))}
        {shouldTruncate && (
          <Box
            // oxlint-disable-next-line react/refs -- reads the anchor DOM node; only used once the popover/anchor has mounted
            aria-owns={popover.open ? anchorRef.current?.id : undefined}
            aria-haspopup="true"
            onMouseEnter={popover.handleOpen}
            onMouseLeave={popover.handleClose}
            ref={anchorRef}
          >
            <EmojiChip label="..." />
          </Box>
        )}
      </Stack>
      <Popover
        id="emoji-popover"
        open={popover.open}
        // oxlint-disable-next-line react/refs -- MUI anchorEl needs the DOM node; the popover only opens after the anchor has mounted
        anchorEl={anchorRef.current}
        onClose={popover.handleClose}
        sx={{ pointerEvents: "none" }}
        disableRestoreFocus
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ m: 1 }}>
          {sorted.map(([emoji, { count }]) => (
            <EmojiChip
              key={emoji}
              emoji={emoji}
              emojiCacheEntry={emojiCache[emoji]}
              label={count}
            />
          ))}
        </Box>
      </Popover>
    </>
  );
};
